import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { makeStyles } from '@widget/toolbox/style';
import { useBreakpoint } from '@widget/utils/useBreakpoint';

import type { ReactFCWithChildren } from '@widget/types/ReactFCWithChildren';

type PulloverProps = {
  isAnimated: boolean;
  background?: React.ReactElement;
  forceEndAnimation?: boolean;
  onTouchMove?: (ev: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd?: (ev: React.TouchEvent<HTMLDivElement>) => void;
};

const START_STEP_OFFSET_POSITION_PROCENT = 40;
const DEFAULT_ARROW_ANGLE = 25;
const ANIMATION_DURATION = 500;
const PULLOVER_HEIGHT = 35;

const useStyles = makeStyles(() => ({
  stepAnimationContainer: css`
    width: 100%;
    overflow: hidden;
    display: flex;
  `,
}));

const StepWrapper = styled.div<{
  animated: boolean;
  transitionCanceled: boolean;
}>`
  width: 50%;
  position: relative;
  padding: ${({ theme }) => theme.spacing(20)} ${({ theme }) => theme.spacing(18)}
    ${({ theme }) => theme.spacing(10)};
  transform: translateX(${(props) => (props.animated ? '0' : '-100%')});
  ${(props) =>
    props.transitionCanceled ? '' : `transition: transform ${ANIMATION_DURATION}ms ease;`}
  z-index: 1;
  background-color: ${({ theme }) => theme.palette.bg.default};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${(props) => props.theme.spacing(10)} ${(props) => props.theme.spacing(9)};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    padding: ${(props) => props.theme.spacing(8)} ${(props) => props.theme.spacing(4)};
    z-index: 2;
  }
`;

const ValuationBackgroundWrapper = styled.div<{ animated: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.animated ? '50%' : '100%')};
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  transition: width ${ANIMATION_DURATION}ms ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    align-items: flex-start;
    padding-top: ${({ theme }) => theme.spacing(10)};
  }
`;

const Overlay = styled.div<{ animated: boolean }>`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  background-color: ${(props) =>
    props.animated ? 'rgba(47, 48, 52, 0.5)' : 'rgba(47, 48, 52, 0)'};
  transition: background-color 0.5s ease;
`;

const Pullover = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${PULLOVER_HEIGHT}px;
  background-color: ${({ theme }) => theme.palette.bg.default};
  position: absolute;
  left: 0;
  top: 0;
  transform: translateY(-100%);
  padding-top: 16px;
`;

const PulloverArrowWrapper = styled.div`
  width: 50px;
  height: 3px;
  position: relative;

  & > div {
    display: block;
    content: '';
    width: 25px;
    height: 3px;
    background-color: rgba(211, 212, 214, 1);
    border-radius: 2px;
    position: absolute;
    top: 0;
  }
`;

const PulloverArrow = ({ angle }: { angle: number }) => {
  return (
    <PulloverArrowWrapper>
      <div style={{ left: 2, transform: `rotate(-${angle}deg)`, transformOrigin: 'top left' }} />
      <div style={{ right: 2, transform: `rotate(${angle}deg)`, transformOrigin: 'top right' }} />
    </PulloverArrowWrapper>
  );
};

export const PulloverSlider: ReactFCWithChildren<PulloverProps> = ({
  children,
  isAnimated,
  background,
  forceEndAnimation,
  onTouchEnd,
  onTouchMove,
}) => {
  const modalRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const classes = useStyles();
  const breakpoint = useBreakpoint();
  const [transitionCanceled, setTransitionCanceled] = useState(false);
  const [reachedTop, setReachedTop] = useState(false);
  const [currentOffset, setCurrentOffset] = useState<number | null>(null);
  const [currentAngle, setCurrentAngle] = useState(DEFAULT_ARROW_ANGLE);

  useEffect(() => {
    modalRef.current = document.querySelector<HTMLElement>('[data-element="modal"]');
    headerRef.current = document.querySelector<HTMLElement>('[data-element="header"]');
  }, []);

  const handleTouchMove = (ev: React.TouchEvent<HTMLDivElement>) => {
    setTransitionCanceled(true);
    const touch = ev.touches[0];
    if (modalRef.current) {
      modalRef.current.style.overflow = 'hidden';
      const scrollTop = Number(modalRef.current?.scrollTop);
      const headerOffset = Number(headerRef.current?.getBoundingClientRect().height);

      const currentTop = touch.clientY - headerOffset + scrollTop;

      if (currentTop > PULLOVER_HEIGHT && (!reachedTop || !forceEndAnimation)) {
        const angleRatio = startOffset / DEFAULT_ARROW_ANGLE;
        setCurrentOffset(currentTop);
        setCurrentAngle(DEFAULT_ARROW_ANGLE - (startOffset - currentTop) / angleRatio);
      } else {
        modalRef.current.style.overflow = '';
      }
    }
    onTouchMove?.(ev);
  };

  const handleTouchEnd = (ev: React.TouchEvent<HTMLDivElement>) => {
    if (modalRef.current) {
      modalRef.current.style.overflow = '';
    }
    setTransitionCanceled(false);
    setReachedTop(true);
    onTouchEnd?.(ev);
  };

  const startOffset = (window.innerHeight / 100) * START_STEP_OFFSET_POSITION_PROCENT;
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';
  const animationEnded = reachedTop || forceEndAnimation;
  const transformY = isAnimated
    ? animationEnded
      ? '0'
      : `${currentOffset || startOffset}px`
    : `calc(100% + ${PULLOVER_HEIGHT}px)`;

  return (
    <div css={classes.stepAnimationContainer as any}>
      <StepWrapper
        style={{
          transform: `translateY(${isMobile ? transformY : 0}) ${isMobile && 'translateX(0)'}`,
        }}
        animated={isAnimated}
        transitionCanceled={transitionCanceled}
      >
        <Pullover onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          <PulloverArrow angle={currentAngle} />
        </Pullover>
        {children}
      </StepWrapper>
      <ValuationBackgroundWrapper animated={isAnimated}>
        <Overlay animated={isAnimated} />
        {background}
      </ValuationBackgroundWrapper>
    </div>
  );
};
